import { Td, Tr } from '@chakra-ui/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Txt } from '../../text';
import { TableColumn, TableGroupRowData } from '../types';
import { DragHandle } from './drag-handle';

export function DraggableTableRow<I extends EntityBase = EntityBase>({ 
  columns, rowIndex, rowData, loading, grouped, collapsed, selectedRow, onRowClick, totalRows, getRowBackgroundColor,
}: {
  columns: TableColumn<I>[],
  rowIndex: number,
  rowData: I,
  loading?: boolean
  grouped?: boolean
  hasGroups?: boolean
  collapsed?: boolean
  selectedRow?: I | TableGroupRowData<I>,
  onRowClick?: (row: I | TableGroupRowData<I>) => void
  totalRows: number;
  getRowBackgroundColor?: (rowData: I, rowIndex: number) => string | undefined;
}) {

  const {
    attributes,
    listeners,
    transform,
    transition,
    setNodeRef,
    isDragging
  } = useSortable({
    id: rowData.id
  });

  if (isDragging) {
    return <Tr ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} >
      <Td 
        colSpan={columns.length} 
        backgroundColor='gray.100'          
        borderColor='gray.300'
        borderTopWidth={0}
        borderBottomWidth={1}
      >&nbsp;</Td>
    </Tr>;
  }

  const backgroundColor = (() => {
    if (rowData.id && (selectedRow as I)?.id === rowData.id) {
      return 'light.300 !important';
    }
    const customColor = getRowBackgroundColor?.(rowData, rowIndex);
    if (customColor) {
      return `${customColor} !important`;
    }
    if (grouped) {
      return 'light.200';
    }
    return 'white !important';
  })();

  return <Tr 
    ref={setNodeRef}
    _hover={{ backgroundColor: onRowClick ? 'gray.200' : 'gray.50' }} 
    opacity={collapsed ? 0 : 1}
    maxHeight={collapsed ? '0px' : 'unset'}
    visibility={collapsed ? 'collapse' : 'visible'}
    lineHeight={collapsed ? '0px' : 'unset'}
    cursor={onRowClick ? 'pointer' : 'default'}
    backgroundColor={backgroundColor}
    style={{ transform: CSS.Transform.toString(transform), transition }}
    onClick={() => onRowClick?.(rowData)}
  >
    {columns.map((column, columnIndex) => {
      if (columnIndex === 0) {
        return <Td 
          key="drag-handle"
          borderColor='gray.300'
          borderTopWidth={0}
          borderBottomWidth={1}
        >
          <DragHandle {...attributes} {...listeners} />
        </Td>;
      }
      if (column.options?.customBodyRender) {
        return <Td 
          key={`${column.name.toString()}_${columnIndex}`} 
          borderColor='gray.300'
          borderTopWidth={0}
          borderBottomWidth={collapsed ? 0 : 1}
          px={5} 
          py={collapsed ? 0 : 2}
          lineHeight={collapsed ? '0px' : 'unset'}
          maxHeight={collapsed ? '0px' : 'unset'}
          opacity={collapsed ? 0 : 1}
          overflow='hidden'
          style={{
            transition: 'all 0.3s linear',
          }}
          css={{
            maxHeight: '0px',
          }}
        >{column.options?.customBodyRender?.(
          rowData[column.name],
          {
            columnIndex,
            rowIndex,
            column,
            rowData,
            loading,
            totalRows,
          }
        )}</Td>;
      }

      return <Td  
        borderColor='gray.300'
        borderTopWidth={0}
        borderBottomWidth={collapsed ? 0 : 1}
        fontSize='sm' 
        lineHeight={collapsed ? '0px' : 'unset'}
        opacity={collapsed ? 0 : 1}
        overflow='hidden'
        px={5} 
        py={collapsed ? 0 : 2}
        key={`${column.name.toString()}_${columnIndex}`} 
        style={{
          transition: 'all 0.3s linear',
        }}
      >
        <Txt whiteSpace='pre-wrap'>{rowData[column.name]}</Txt>
      </Td>;
    })}
  </Tr>;
}